import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import { OnboardingChapter } from '../../../pure-js/enums/OnboardingChapter'
import { Button } from '../interaxDesignSystemComponents'
import useAppState from '../hooks/useAppState'
import { useUpdateUser } from '../apiLayer'
import { useLogout } from '../hooks/useLogout'

export default function Onboarding() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { state, setState } = useAppState()
  const [currentStep, setCurrentStep] = useState(0)
  const logout = useLogout(() => navigate('/'))

  // Use mutation hook for updating user
  const updateUserMutation = useUpdateUser()

  const onboardingSteps = [
    {
      title: t('auth.joinTeam'),
      description: t('auth.welcomeDescription'),
      icon: '👥',
      chapter: OnboardingChapter.Introduction
    },
    {
      title: t('auth.joinTeam'),
      description: t('auth.welcomeDescription'),
      icon: '👥',
      chapter: OnboardingChapter.Assessment
    },
    {
      title: t('auth.joinTeam'),
      description: t('auth.welcomeDescription'),
      icon: '💡',
      chapter: OnboardingChapter.GameIntroduction
    },
    {
      title: t('auth.getStarted'),
      description: t('auth.goToDashboardDescription'),
      icon: '🚀',
      chapter: null
    }
  ]

  const currentStepData = onboardingSteps[currentStep]
  const isLastStep = currentStep === onboardingSteps.length - 1

  const handleNext = async () => {
    if (isLastStep) {
      // Complete onboarding and update user state
      try {
        await updateUserMutation.mutateAsync({
          id: state.user.id,
          userData: {
            completedOnboardingSteps: [OnboardingChapter.GameIntroduction]
          }
        })

        // Update local state
        setState({
          ...state,
          user: {
            ...state.user,
            completedOnboardingSteps: [OnboardingChapter.GameIntroduction]
          }
        })

        navigate('/signedin')
      } catch (error) {
        // Still navigate even if update fails
        navigate('/signedin')
      }
    } else {
      // Update app state with current chapter
      const currentChapter = currentStepData.chapter
      if (currentChapter) {
        setState({
          ...state,
          currentOnboardingChapter: currentChapter
        })
      }
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }
  return (
    <div className="min-h-screen bg-off-black flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header with Back Button and Sign Out Button (only on first step) */}
        {currentStep === 0 && (
          <div className="flex justify-between items-center">
            <Button variant="link" onClick={() => navigate('/')} className="text-thunder-grey hover:text-white">
              ← {t('common.back')}
            </Button>
            <Button variant="link" onClick={logout} className="text-thunder-grey hover:text-white">
              {t('auth.signOut')}
            </Button>
          </div>
        )}

        {/* Background Box */}
        <div className="bg-off-black border border-thunder-grey rounded-lg p-8 space-y-8">
          {/* Progress Indicator */}
          <div className="flex justify-center space-x-2">
            {onboardingSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${index <= currentStep ? 'bg-sun' : 'bg-thunder-grey'}`}
              />
            ))}
          </div>

          {/* Step Content */}
          <div className="text-center space-y-6">
            <div className="text-6xl mb-4">{currentStepData.icon}</div>

            <h1 className="text-3xl font-semibold text-white">{currentStepData.title}</h1>

            <p className="text-thunder-grey text-lg leading-relaxed">{currentStepData.description}</p>
          </div>

          {/* Navigation Buttons */}
          <div className="flex space-x-4">
            {currentStep > 0 && (
              <Button variant="secondary" onClick={handlePrevious} className="flex-1 h-12">
                {t('auth.previous')}
              </Button>
            )}

            <Button variant="primary" onClick={handleNext} className={currentStep > 0 ? 'flex-1 h-12' : 'w-full h-12'}>
              {isLastStep ? t('auth.getStarted') : t('auth.next')}
            </Button>
          </div>

          {/* Skip Option */}
          {!isLastStep && (
            <div className="text-center">
              <button
                onClick={() => {
                  navigate('/signedin')
                }}
                className="text-thunder-grey hover:text-white font-medium"
              >
                {t('auth.skip')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
